/**
 * WeatherStationChart: the pour's site weather from its weather station,
 * over time, as three stacked panes on one time axis: air temperature with
 * its dew point, relative humidity, and wind. Each measure keeps its own
 * scale; one crosshair and a shared tooltip run through all three.
 *
 * The readings are the ones the worker's weather job stitches onto the pour
 * (PourWeatherTrack, GET /pour/{id}/weather, see useWeatherPoints), so a
 * pour with only a weather station (Onsite Weather Conditions, no concrete
 * location) has a weather chart too. Each refresh asks only for the
 * readings after the newest one held. A stretch where the station sent
 * nothing breaks the lines instead of bridging them. The pour's start is
 * marked. Wind arrives in km/h and is shown in the company's unit.
 */
import React, { useMemo } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { parseUtc } from '../../utils/datetime';
import { dewPoint } from '../../utils/siteConditions';
import { windInUnit, type WindSpeedUnit } from '../../utils/windSpeed';
import { getBaseTimeSeriesOptions } from './chartOptions';
import { SERIES_COLORS } from './overlayModel';
import { useWeatherPoints, type WeatherPoint } from './useWeatherPoints';

interface WeatherStationChartProps {
  pourId: string;
  timezoneIana?: string;
  /** Bumped by the page on each refresh: the newer readings are fetched. */
  refreshSignal?: number;
  windSpeedUnit?: WindSpeedUnit;
  /** When the pour started (naive UTC); marked on the time axis. */
  pourStartAt?: string | null;
}

const CHART_HEIGHT = 560;

const RANGE_BUTTONS: Highcharts.RangeSelectorButtonsOptions[] = [
  { type: 'hour', count: 6, text: '6h' },
  { type: 'day', count: 1, text: '24h' },
  { type: 'day', count: 2, text: '48h' },
  { type: 'all', text: 'All' },
];

/** Pane placement down the plot: temperature gets the most room. */
const PANES = {
  temp: { top: '0%', height: '42%' },
  rh: { top: '48%', height: '22%' },
  wind: { top: '76%', height: '24%' },
} as const;

/** A break wherever readings are over four reporting intervals apart. */
const GAP_SIZE = 4;

export const WeatherStationChart: React.FC<WeatherStationChartProps> = ({
  pourId,
  timezoneIana,
  refreshSignal,
  windSpeedUnit,
  pourStartAt,
}) => {
  const { points, loading, error } = useWeatherPoints(pourId, refreshSignal);

  const windLabel = windSpeedUnit === 'mph' ? 'mph' : 'km/h';
  const startMs = pourStartAt ? parseUtc(pourStartAt).getTime() : NaN;
  const options = useMemo<Highcharts.Options>(() => {
    const series = (index: 1 | 2 | 3, convert?: (v: number) => number | null) =>
      points
        .filter((p) => p[index] !== null)
        .map((p) => [p[0], convert ? convert(p[index] as number) : (p[index] as number)]);
    const dew = points
      .map((p: WeatherPoint) => [p[0], dewPoint(p[1], p[2])] as const)
      .filter((p): p is readonly [number, number] => p[1] !== null)
      .map((p) => [p[0], Math.round(p[1] * 10) / 10]);
    const base = getBaseTimeSeriesOptions('', {
      timezoneIana,
      height: CHART_HEIGHT,
      rangeButtons: RANGE_BUTTONS,
      selectedRangeButton: 1,
    });
    const axis = (title: string, pane: { top: string; height: string }, extra: Highcharts.YAxisOptions = {}) => ({
      title: { text: title },
      ...pane,
      offset: 0,
      opposite: false,
      lineWidth: 1,
      ...extra,
    });
    return {
      ...base,
      title: { text: undefined },
      legend: { enabled: true },
      xAxis: {
        ...(base.xAxis as Highcharts.XAxisOptions),
        plotLines: Number.isNaN(startMs)
          ? []
          : [{
            value: startMs,
            color: '#79A82A',
            width: 1.5,
            dashStyle: 'Dash',
            zIndex: 3,
            label: { text: 'Pour started', style: { color: '#4f7419', fontSize: '11px' } },
          }],
      },
      // A stock chart draws no markers, so a new station's first readings
      // would not show at all: mark the points while there are only a few.
      plotOptions: {
        series: { marker: { enabled: points.length < 3, radius: 3 }, gapSize: GAP_SIZE },
      },
      yAxis: [
        axis('Air temp (°C)', PANES.temp),
        axis('Humidity (%RH)', PANES.rh, { min: 0, max: 100, tickInterval: 50 }),
        axis(`Wind (${windLabel})`, PANES.wind, { min: 0 }),
      ],
      tooltip: { ...base.tooltip, shared: true, split: false, valueDecimals: 1 },
      series: [
        {
          type: 'line', name: 'Air temperature', yAxis: 0, color: SERIES_COLORS[0],
          data: series(1), tooltip: { valueSuffix: ' °C' },
        },
        {
          type: 'line', name: 'Dew point', yAxis: 0, color: SERIES_COLORS[4], dashStyle: 'ShortDash', lineWidth: 1.5,
          data: dew, tooltip: { valueSuffix: ' °C' },
        },
        {
          type: 'line', name: 'Humidity', yAxis: 1, color: SERIES_COLORS[1],
          data: series(2), tooltip: { valueSuffix: ' %RH', valueDecimals: 0 },
        },
        {
          type: 'line', name: 'Wind', yAxis: 2, color: SERIES_COLORS[2],
          data: series(3, (kmh) => windInUnit(kmh, windSpeedUnit)), tooltip: { valueSuffix: ` ${windLabel}` },
        },
      ],
    };
  }, [points, timezoneIana, windSpeedUnit, windLabel, startMs]);

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Paper>
    );
  }
  if (error && points.length === 0) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (points.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }} data-testid="weather-station-chart-empty">
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          No data available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No weather station readings for this pour yet.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper elevation={2} sx={{ p: 2 }} data-testid="weather-station-chart">
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Site Weather vs Time
        </Typography>
      </Box>
      <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={options} />
    </Paper>
  );
};
